import { useEffect, useMemo, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Inbox,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
} from 'lucide-react';
import { LeadStatus } from '@pms/shared';
import { usePlatformLeads } from '@/hooks/api/usePlatform';
import type { PlatformLead } from '@/services/api/platform.api';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';

interface LeadPickerDialogProps {
  onClose: () => void;
  onSelect: (lead: PlatformLead) => void;
}

/** Leads that are still open to onboard — not yet converted or closed. */
const OPEN_STATUSES: LeadStatus[] = [LeadStatus.NEW, LeadStatus.CONTACTED];

/**
 * Modal that lists still-open owner leads so the admin can start a property
 * prefilled from one. Selecting a lead is prefill-only — it does NOT change the
 * lead's status (approve/onboard stays on the Leads page). Mount only while
 * open so the leads query doesn't fire on the Properties page load.
 */
export function LeadPickerDialog({ onClose, onSelect }: LeadPickerDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = 'lead-picker-title';
  const descId = 'lead-picker-desc';

  const { data, isLoading, isError, refetch } = usePlatformLeads('all');

  const openLeads = useMemo(
    () => (data?.items ?? []).filter((l) => OPEN_STATUSES.includes(l.status)),
    [data],
  );

  useEffect(() => {
    closeRef.current?.focus();

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative z-10 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl border border-gray-200"
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-gray-900">
              Start from a submission
            </h2>
            <p id={descId} className="mt-0.5 text-sm text-gray-600">
              Pick an open owner lead to prefill the new property.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            Cancel
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={22} className="animate-spin text-brand-600" aria-hidden="true" />
              <span className="ml-2 text-sm text-gray-500">Loading submissions…</span>
            </div>
          ) : isError ? (
            <div className="py-12 text-center">
              <AlertCircle size={22} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
              <p className="text-sm text-red-700 mb-4">Unable to load submissions.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                Retry
              </button>
            </div>
          ) : openLeads.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No open submissions"
              description="Owner leads that are still new or in progress will appear here."
            />
          ) : (
            <ul className="space-y-2" aria-label="Open owner leads">
              {openLeads.map((lead) => (
                <li key={lead.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(lead)}
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors',
                      'hover:border-brand-300 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-sm font-semibold text-gray-900">
                          {lead.propertyName}
                        </span>
                        <span className="text-xs text-gray-500">· {lead.ownerName}</span>
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} className="text-gray-400" aria-hidden="true" />
                          {lead.city}
                          {lead.country ? `, ${lead.country}` : ''}
                        </span>
                        <span className="inline-flex items-center gap-1 break-all">
                          <Mail size={12} className="text-gray-400" aria-hidden="true" />
                          {lead.email}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Phone size={12} className="text-gray-400" aria-hidden="true" />
                          {lead.phone}
                        </span>
                      </span>
                    </span>
                    <ChevronRight
                      size={16}
                      className="shrink-0 text-gray-300 group-hover:text-brand-500"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
